import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  PageContainer,
  Header,
  Container,
  Sidebar,
  Progress,
  Steps,
  Step,
  FormContainer,
  FormSection,
  FormGrid,
  InputGroup,
  ButtonGroup,
  Button,
  ImageUpload,
  Preview,
  PreviewHeader,
  PreviewContent
} from './styles';

const NewAgency = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const autocompleteTimeout = React.useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: null,
    logoFile: null,
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    phone: '',
    email: '',
    website: '',
    facebook: '',
    instagram: '',
    foundedYear: '',
    license: '',
    specialties: [],
    operatingHours: {
      weekday: '',
      saturday: '',
      sunday: ''
    }
  });

  const steps = [
    { number: 1, text: 'Informações Básicas' },
    { number: 2, text: 'Localização' },
    { number: 3, text: 'Contato' },
    { number: 4, text: 'Detalhes Adicionais' }
  ];

  const progress = (currentStep / steps.length) * 100;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressAutocomplete = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, address: value }));
    if (autocompleteTimeout.current) clearTimeout(autocompleteTimeout.current);
    if (!value) {
      setAddressSuggestions([]);
      return;
    }
    autocompleteTimeout.current = setTimeout(() => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        return;
      }
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input: value, language: 'pt' },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            setAddressSuggestions(predictions || []);
          } else {
            setAddressSuggestions([]);
          }
        }
      );
    }, 400);
  };

  const handleSelectAddress = (suggestion) => {
    setAddressSuggestions([]);
    setFormData(prev => ({ ...prev, address: suggestion.description }));
    if (!window.google || !window.google.maps || !window.google.maps.places) return;
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails({ placeId: suggestion.place_id, language: 'pt' }, (data, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && data) {
        const { address_components, geometry } = data;
        let cidade = '', estado = '', pais = '', cep = '';
        address_components.forEach(comp => {
          if (comp.types.includes('locality')) cidade = comp.long_name;
          if (!cidade && comp.types.includes('administrative_area_level_2')) cidade = comp.long_name;
          if (comp.types.includes('administrative_area_level_1')) estado = comp.short_name || comp.long_name;
          if (comp.types.includes('country')) pais = comp.short_name;
          if (comp.types.includes('postal_code')) cep = comp.long_name;
        });
        setFormData(prev => ({
          ...prev,
          address: suggestion.description,
          city: cidade,
          state: estado,
          country: pais,
          postalCode: cep,
          latitude: geometry.location.lat(),
          longitude: geometry.location.lng()
        }));
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          // mantém preview base64 para a UI, mas guarda também o File para envio
          logo: reader.result,
          logoFile: file
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: null,
      logoFile: null
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    // Monta FormData para reduzir o tamanho do payload (evitar 413) e enviar arquivo corretamente
    const fd = new FormData();
    fd.append('nomeIMOBILIARIA', formData.name || '');
    fd.append('enderecoIMOBILIARIA', formData.address || '');
    // Endereço detalhado
    if (formData.city) fd.append('cidadeIMOBILIARIA', formData.city);
    if (formData.state) fd.append('estadoIMOBILIARIA', formData.state);
    if (formData.country) fd.append('paisIMOBILIARIA', formData.country);
    if (formData.postalCode) fd.append('cepIMOBILIARIA', formData.postalCode);
    fd.append('telefoneIMOBILIARIA', formData.phone || '');
    fd.append('emailIMOBILIARIA', formData.email || '');
    fd.append('descricaoIMOBILIARIA', formData.description || '');
    // ativo como '1' ou '0' (padrão '1' por enquanto)
    fd.append('ativoIMOBILIARIA', '1');
    if (formData.logoFile) {
      fd.append('imagemIMOBILIARIA', formData.logoFile, formData.logoFile.name);
    }
    // Campos opcionais (omita se vazio)
    if (formData.website) fd.append('siteIMOBILIARIA', formData.website);
    if (formData.facebook) fd.append('facebookIMOBILIARIA', formData.facebook);
    if (formData.instagram) fd.append('instagramIMOBILIARIA', formData.instagram);
    if (formData.country) fd.append('idiomaIMOBILIARIA', formData.country === 'BR' ? 'pt-BR' : 'pt-PT');
    if (formData.fax) fd.append('faxIMOBILIARIA', formData.fax);
    if (formData.twitter) fd.append('twitterIMOBILIARIA', formData.twitter);
    if (formData.linkedin) fd.append('linkedinIMOBILIARIA', formData.linkedin);
    // Documentos/Extras
    if (formData.license) fd.append('licencaIMOBILIARIA', formData.license);
    if (formData.foundedYear) fd.append('anoIMOBILIARIA', String(formData.foundedYear));
    // Horários: envia JSON consolidado e, opcionalmente, campos separados
    try {
      const hours = formData.operatingHours || {};
      if (hours && (hours.weekday || hours.saturday || hours.sunday)) {
        fd.append('operatingHoursIMOBILIARIA', JSON.stringify({
          weekday: hours.weekday || '',
          saturday: hours.saturday || '',
          sunday: hours.sunday || ''
        }));
        if (hours.weekday) fd.append('horarioSemanaIMOBILIARIA', hours.weekday);
        if (hours.saturday) fd.append('horarioSabadoIMOBILIARIA', hours.saturday);
        if (hours.sunday) fd.append('horarioDomingoIMOBILIARIA', hours.sunday);
      }
    } catch { /* noop */ }
    // Especialidades (se existir no estado)
    if (Array.isArray(formData.specialties) && formData.specialties.length > 0) {
      try { fd.append('specialtiesIMOBILIARIA', JSON.stringify(formData.specialties)); } catch { /* noop */ }
    }

    try {
      // Recupera token para autenticação
      const token = localStorage.getItem('auth_token');
      if (!token) {
        await Swal.fire({
          icon: 'warning',
          title: 'Sessão expirada',
          text: 'Faça login novamente para criar uma agência.',
          confirmButtonText: 'Ir para login'
        });
        navigate('/login');
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_URL_API}/imobiliarias`, {
        method: 'POST',
        headers: {
          // não definir Content-Type manualmente; o browser define com boundary para multipart
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: fd,
      });

      if (response.ok) {
        // Tenta obter o ID da agência criada e persistir como selecionada
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json();
            const newId = (data?.codigoIMOBILIARIA ?? data?.id ?? data?.codigo ?? '').toString();
            if (newId) {
              try { localStorage.setItem('selected_imobiliaria', newId); } catch { /* noop */ }
            }
          }
        } catch { /* noop */ }

        // Guarda uma dica para a tela de listagem selecionar a agência recé m-criada
        try {
          localStorage.setItem('last_agency_hint', JSON.stringify({
            name: formData.name || '',
            email: formData.email || ''
          }));
        } catch { /* noop */ }

        navigate('/dashboard/agency');
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Agência criada com sucesso!',
          confirmButtonText: 'OK'
        });
      } else {
        let detail = response.statusText || 'Falha ao criar a agência.';
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json();
            detail = data?.message || data?.error || JSON.stringify(data);
          } else {
            detail = await response.text();
          }
        } catch { /* noop */ }
        console.error('Failed to create agency:', detail);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: detail,
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error creating agency:', error);
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Erro inesperado ao criar a agência.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormSection>
            <h2>Informações Básicas</h2>
            <FormGrid>
              <InputGroup className="full-width">
                <label>Logo da Agência</label>
                <ImageUpload>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {!formData.logo ? (
                    <>
                      <div className="icon">📷</div>
                      <div className="text">
                        Clique ou arraste uma imagem<br />
                        <small>Tamanho recomendado: 500x500px</small>
                      </div>
                    </>
                  ) : (
                    <>
                      <img src={formData.logo} alt="Preview" className="preview" />
                      <button className="remove" onClick={handleRemoveLogo}>×</button>
                    </>
                  )}
                </ImageUpload>
              </InputGroup>
              <InputGroup className="full-width">
                <label>Nome da Agência</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ex: Sebook Imóveis"
                  required
                />
                <div className="hint">Este será o nome principal da sua agência</div>
              </InputGroup>
              <InputGroup className="full-width">
                <label>Descrição</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descreva sua agência em detalhes..."
                  required
                />
                <div className="hint">Uma boa descrição ajuda a destacar sua agência</div>
              </InputGroup>
            </FormGrid>
          </FormSection>
        );

      case 2:
        return (
          <FormSection>
            <h2>Localização</h2>
            <FormGrid>
              <InputGroup className="full-width" style={{ position: 'relative' }}>
                <label>Endereço</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleAddressAutocomplete}
                  placeholder="Digite o endereço e selecione na lista"
                  autoComplete="off"
                  required
                  style={{ padding: 12, border: '2px solid #e2e8f0', borderRadius: 8, fontSize: '1rem' }}
                />
                {addressSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', background: '#fff', zIndex: 10, border: '1px solid #e2e8f0', width: '100%', borderRadius: 8, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    {addressSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                        onMouseEnter={(e) => e.target.style.background = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.background = '#fff'}
                        onClick={() => handleSelectAddress(suggestion)}
                      >
                        {suggestion.description}
                      </div>
                    ))}
                  </div>
                )}
              </InputGroup>
              <InputGroup>
                <label>Cidade</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Distrito</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>País</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecione um país</option>
                  <option value="PT">Portugal</option>
                  <option value="BR">Brasil</option>
                  <option value="ES">Espanha</option>
                </select>
              </InputGroup>
              <InputGroup>
                <label>Código Postal</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                />
              </InputGroup>
            </FormGrid>
            {formData.latitude && formData.longitude && (
              <div style={{ marginTop: 24 }}>
                <iframe
                  title="Mapa"
                  width="100%"
                  height="300"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: 12 }}
                  src={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}&hl=pt-BR&z=16&output=embed`}
                  allowFullScreen
                ></iframe>
              </div>
            )}
          </FormSection>
        );

      case 3:
        return (
          <FormSection>
            <h2>Contato</h2>
            <FormGrid>
              <InputGroup>
                <label>Telefone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+351 123 456 789"
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="contato@suaagencia.com"
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="www.suaagencia.com"
                />
                <div className="hint">Opcional</div>
              </InputGroup>
              <InputGroup>
                <label>Facebook</label>
                <input
                  type="url"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  placeholder="facebook.com/suaagencia"
                />
                <div className="hint">Opcional</div>
              </InputGroup>
              <InputGroup>
                <label>Instagram</label>
                <input
                  type="url"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  placeholder="instagram.com/suaagencia"
                />
                <div className="hint">Opcional</div>
              </InputGroup>
            </FormGrid>
          </FormSection>
        );

      case 4:
        return (
          <FormSection>
            <h2>Detalhes Adicionais</h2>
            <FormGrid>
              <InputGroup>
                <label>Ano de Fundação</label>
                <input
                  type="number"
                  name="foundedYear"
                  value={formData.foundedYear}
                  onChange={handleChange}
                  placeholder="2024"
                  required
                />
              </InputGroup>
              <InputGroup>
                <label>Licença (AMI)</label>
                <input
                  type="text"
                  name="license"
                  value={formData.license}
                  onChange={handleChange}
                  placeholder="AMI 12345"
                  required
                />
                <div className="hint">Número de registro obrigatório</div>
              </InputGroup>
              <InputGroup className="full-width">
                <label>Horário de Funcionamento</label>
                <FormGrid>
                  <InputGroup>
                    <label>Dias Úteis</label>
                    <input
                      type="text"
                      name="weekday"
                      value={formData.operatingHours.weekday}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        operatingHours: {
                          ...prev.operatingHours,
                          weekday: e.target.value
                        }
                      }))}
                      placeholder="9:00 - 18:00"
                    />
                  </InputGroup>
                  <InputGroup>
                    <label>Sábado</label>
                    <input
                      type="text"
                      name="saturday"
                      value={formData.operatingHours.saturday}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        operatingHours: {
                          ...prev.operatingHours,
                          saturday: e.target.value
                        }
                      }))}
                      placeholder="10:00 - 14:00"
                    />
                  </InputGroup>
                  <InputGroup>
                    <label>Domingo</label>
                    <input
                      type="text"
                      name="sunday"
                      value={formData.operatingHours.sunday}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        operatingHours: {
                          ...prev.operatingHours,
                          sunday: e.target.value
                        }
                      }))}
                      placeholder="Fechado"
                    />
                  </InputGroup>
                </FormGrid>
              </InputGroup>
            </FormGrid>
          </FormSection>
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Nova Agência</h1>
      </Header>

      <Container>
        <Sidebar>
          <Progress progress={progress}>
            <h2>Progresso</h2>
            <div className="progress-bar">
              <div className="fill" />
            </div>
            <div className="steps-completed">
              {currentStep} de {steps.length} etapas
            </div>
          </Progress>
          <Steps>
            {steps.map((step) => (
              <Step
                key={step.number}
                active={currentStep === step.number}
                completed={currentStep > step.number}
                onClick={() => setCurrentStep(step.number)}
              >
                <span className="number">{step.number}</span>
                <span className="text">{step.text}</span>
              </Step>
            ))}
          </Steps>
        </Sidebar>
        <div>
          <form onSubmit={handleSubmit}>
            <FormContainer>
              {renderStepContent()}
              <ButtonGroup>
                <div>
                  <Button 
                    type="button" 
                    className="secondary"
                    onClick={() => navigate('/dashboard/agency')}
                  >
                    Cancelar
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {currentStep > 1 && (
                    <Button type="button" className="secondary" onClick={handlePrevious}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      Voltar
                    </Button>
                  )}
                  {currentStep < steps.length ? (
                    <Button type="button" className="primary" onClick={handleNext}>
                      Próximo
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Button>
                  ) : (
                    <Button type="submit" className="primary">
                      Criar Agência
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 13l4 4L19 7"/>
                      </svg>
                    </Button>
                  )}
                </div>
              </ButtonGroup>
            </FormContainer>
          </form>

          <Preview>
            <PreviewHeader>
              <h2>Preview da Agência</h2>
            </PreviewHeader>
            <PreviewContent>
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {formData.logo ? (
                  <img 
                    src={formData.logo} 
                    alt="Logo" 
                    style={{ width: '120px', height: '120px', borderRadius: '12px', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ 
                    width: '120px', 
                    height: '120px', 
                    background: '#34574A',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '2rem'
                  }}>
                    {formData.name ? formData.name.charAt(0) : 'A'}
                  </div>
                )}
                <div>
                  <h3 style={{ fontSize: '1.5rem', color: '#333', margin: '0 0 8px 0' }}>
                    {formData.name || 'Nome da Agência'}
                  </h3>
                  <p style={{ color: '#666', margin: '0 0 16px 0' }}>
                    {formData.description || 'Descrição da agência...'}
                  </p>
                  <div style={{ display: 'flex', gap: '24px', color: '#666', fontSize: '0.875rem' }}>
                    {formData.city && formData.state && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {formData.city}, {formData.state}
                      </div>
                    )}
                    {formData.foundedYear && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px' }}>
                          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        Desde {formData.foundedYear}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </PreviewContent>
          </Preview>
        </div>
      </Container>
    </PageContainer>
  );
};

export default NewAgency;